import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GRADIENT_KEYS, raceConditions, SURFACE_KEYS, TRACTION_CONTROL } from '../../domain/race/raceConditions';

/**
 * What is being compared, and under what (SPEC-008 PR-8.5, D-17).
 *
 * All five values live in the QUERY STRING, written with `replace`, exactly as
 * the garage's filters do in `browseState.js` and for the same two reasons. A
 * comparison is worth SENDING somebody — "the F40 and the 959, in the snow" is
 * the whole of what makes the link interesting — and `replace` means changing
 * the surface four times does not cost four presses of the back button to leave
 * the screen. The pair is a description of what this screen is showing rather
 * than a series of steps in a journey.
 *
 * Everything read here arrives from a URL anybody can type, so nothing is
 * trusted. The conditions are parsed against their closed vocabularies by
 * `raceConditions`, and the two car ids are matched against the catalogue that
 * actually loaded — an id for a car that has been retired from the content pack
 * falls back to the default pair rather than to a blank screen.
 *
 * Parameters this screen does not own are preserved, for the reason the garage
 * preserves them: a link may carry a campaign tag or a fragment, and a screen
 * that rewrote the whole query would drop it on the first tap.
 */

/** The four parameters this screen owns. The rest of the query is not its business. */
const PAIR_KEYS = ['a', 'b'];
const CONDITION_KEYS = { surface: SURFACE_KEYS, gradient: GRADIENT_KEYS, tractionControl: TRACTION_CONTROL };

/** `tc` in the URL, because `tractionControl=on` is a lot of address for one bit. */
const PARAM_NAMES = { surface: 'surface', gradient: 'road', tractionControl: 'tc' };

/**
 * The pair a reader who arrived with no opinion gets.
 *
 * The first two cars of the catalogue AS LOADED, not two ids written down here.
 * A hard-coded default pair is a pair that silently becomes a blank screen the
 * day a record is renamed, and this is the one screen in the app whose empty
 * state has no content of its own to fall back on.
 *
 * They are deliberately not chosen for being interesting. The catalogue's own
 * order is the order the garage draws, so the pair the reader lands on is the
 * pair they would have picked first anyway, and every alternative is this
 * module having an opinion about which two cars are worth comparing.
 */
function defaultPair(cars) {
    return [cars[0]?.id ?? null, cars[1]?.id ?? null];
}

/**
 * Two ids the catalogue can actually supply, in the order the URL named them.
 *
 * A pair that came back with the same car twice is not rejected. It is a
 * perfectly legible thing for a reader to do — every figure ties and the race is
 * a dead heat — and refusing it would mean a picker that has to grey out one
 * option in the other's list.
 */
export function readPairFromSearch(search, cars) {
    if (!Array.isArray(cars) || cars.length < 2) {
        return [null, null];
    }

    const params = new URLSearchParams(search || '');
    const fallback = defaultPair(cars);

    return PAIR_KEYS.map((key, index) => {
        const asked = params.get(key);

        return cars.some((car) => car.id === asked) ? asked : fallback[index];
    });
}

export function readConditionsFromSearch(search) {
    const params = new URLSearchParams(search || '');

    return raceConditions(Object.keys(CONDITION_KEYS).reduce((conditions, key) => ({
        ...conditions,
        [key]: params.get(PARAM_NAMES[key]),
    }), {}));
}

/**
 * The query a comparison is addressed by.
 *
 * The pair is always written; the conditions are written only where they differ
 * from the dry, flat, aided default. So the address a reader shares after
 * changing nothing is `?a=…&b=…` rather than five parameters restating what the
 * app would have assumed — the same argument `writeFiltersToSearch` makes for
 * dropping an empty facet.
 */
export function writeCompareToSearch(pair, conditions, existing) {
    const params = new URLSearchParams(existing || '');

    PAIR_KEYS.forEach((key, index) => {
        if (pair[index]) {
            params.set(key, pair[index]);
        } else {
            params.delete(key);
        }
    });

    Object.entries(CONDITION_KEYS).forEach(([key, vocabulary]) => {
        if (conditions[key] === vocabulary[0]) {
            params.delete(PARAM_NAMES[key]);
        } else {
            params.set(PARAM_NAMES[key], conditions[key]);
        }
    });

    return params.toString();
}

/**
 * The comparison, held in the address bar.
 *
 * Returns the two RECORDS rather than the two ids, because every caller wants
 * the car and none of them wants to look it up — and doing the lookup here is
 * what keeps "an id the catalogue does not have" a single decision in a single
 * place rather than a null check in four components.
 */
export function useCompareState(cars) {
    const [searchParams, setSearchParams] = useSearchParams();
    const search = searchParams.toString();
    const pair = useMemo(() => readPairFromSearch(search, cars), [search, cars]);
    const conditions = useMemo(() => readConditionsFromSearch(search), [search]);
    const selected = useMemo(
        () => pair.map((id) => cars?.find((car) => car.id === id) ?? null),
        [pair, cars],
    );

    const write = useCallback((nextPair, nextConditions) => {
        setSearchParams(writeCompareToSearch(nextPair, nextConditions, search), { replace: true });
    }, [setSearchParams, search]);

    const setCar = useCallback((side, carId) => {
        write(pair.map((id, index) => (index === side ? carId : id)), conditions);
    }, [write, pair, conditions]);

    const setCondition = useCallback((key, value) => {
        write(pair, { ...conditions, [key]: value });
    }, [write, pair, conditions]);

    return { cars: selected, conditions, setCar, setCondition };
}
